import * as React from "react"
import { Suspense, useMemo } from "react"
import {
  createBrowserRouter,
  RouterProvider,
  Outlet,
  Path,
  NavigateOptions,
} from "react-router-dom"
import { useAppDispatch, useAppNavigate } from "./reduxStore/hooks"
import { useSelector } from "react-redux"
import {
  fetchProviderTableData,
  fetchPracticeTableData,
} from "./domain/CMS/loader"
import {
  selectContentDataStatus,
  selectProviderTableDataStatus,
  selectPracticeTableDataStatus,
} from "./domain/CMS/model"
import {
  AuthStatus,
  selectAuthenticationStatus,
} from "./features/authentication/authenticationSlice"
import * as routes from "./routes"
import { appRoutes } from "./routes"
import { DataStatus } from "./domain/enums"

// Non-lazy-loaded components
import WithAuthentication from "./features/authentication/components/WithAuthentication"
import { DataFetchingRouter } from "./features/dataFetchingRouter/DataFetchingRouter"
import NavigationListener from "./features/navigationListener/NavigationListener"
import ContentLayout from "./features/layout/ContentLayout"
import { InternalError } from "./pages/InternalError"
import { ErrorBoundary } from "./features/shared/components/ErrorBoundary"
import { ErrorMessage } from "./features/shared/components/ErrorMessage"
import {
  appInsights,
  initializeAppInsights,
} from "./features/logging/ApplicationInsightsService"
import { lazyLoad } from "./lazyLoad"
import { NavigationProvider } from "./features/navigation/NavigationContext"
import { getProtectedConfig } from "./features/authentication/apiClient"
import Loading from "./pages/Loading"
import { initializeCMSMode } from "./domain/CMS/cmsConfig"
import { reinitializeFileDetails } from "./domain/CMS/fileService"
import appInsightsLogger from "./features/logging/appInsightsLogger"
import { Content } from "./domain/CMS/schema"
import { useCMSReady } from "./domain/CMS/useCMSReady"
import { WithHASupportAuthentication } from "./features/authentication/components/WithHASupportAuthentication"
import AppScrollStrategyProvider from "./features/shared/components/AppScrollStrategy"
import Spinner from "./features/shared/components/Spinner"

// Lazy-loaded components
const ContactUs = lazyLoad(() => import("./pages/ContactUs"))
const Dashboard = lazyLoad(() => import("./pages/Dashboard"))
const Landing = lazyLoad(() => import("./pages/Landing"))
const TermsOfUse = lazyLoad(() => import("./pages/TermsOfUse"))
const HelpPage = lazyLoad(() => import("./pages/HelpPage"))
const UnderConstruction = lazyLoad(() =>
  import("./pages/UnderConstruction").then((module) => ({
    default: module.UnderConstruction,
  })),
)
const QA = lazyLoad(() => import("./pages/QA/QA"))
const TermsOfUsePage = lazyLoad(() => import("./pages/TermsOfUsePage"))
const PrivacyPolicyPage = lazyLoad(() => import("./pages/PrivacyPolicyPage"))
const UserRoles = lazyLoad(() => import("./pages/UserRoles"))
const Support = lazyLoad(() => import("./pages/Support/Support"))
const ARSupport = lazyLoad(() => import("./pages/Support/ARSupport"))
const PracticeProfilePage = lazyLoad(() =>
  import("./pages/PracticeProfile/PracticeProfilePage").then((module) => ({
    default: module.PracticeProfilePage,
  })),
)
const TinVerification = lazyLoad(
  () =>
    import("./features/practiceProfile/TaxInformationBreadcrumb/Breadcrumbs/TinVerification"),
)
const W9Information = lazyLoad(
  () =>
    import("./features/practiceProfile/TaxInformationBreadcrumb/Breadcrumbs/W9Information"),
)
const TaxInformationBreadCrumbs = lazyLoad(
  () =>
    import("./features/practiceProfile/TaxInformationBreadcrumb/TaxInformationV3"),
)
const Liability = lazyLoad(
  () => import("./features/practiceProfile/LiabilityInfo/LiabilityV2"),
)
const Offices = lazyLoad(
  () => import("./features/practiceProfile/OfficeInfo/Offices"),
)
const PracticeAgreement = lazyLoad(
  () =>
    import("./features/practiceProfile/PracticeAgreement/PracticeAgreement"),
)
const WithHaAdminAuthentication = lazyLoad(() =>
  import("./features/authentication/components/WithHAUserAuthentication").then(
    (module) => ({ default: module.WithHaAdminAuthentication }),
  ),
)
const PracticeAddress = lazyLoad(
  () =>
    import("./features/practiceProfile/PracticeInfo/components/PracticeAddress"),
)
const PracticeContact = lazyLoad(
  () =>
    import("./features/practiceProfile/PracticeInfo/components/PracticeContact"),
)
const PracticeDetails = lazyLoad(
  () =>
    import("./features/practiceProfile/PracticeInfo/components/PracticeDetails"),
)
const PracticeInformation = lazyLoad(() =>
  import("./features/practiceProfile/PracticeInfo/PracticeInformation").then(
    (module) => ({ default: module.PracticeInformation }),
  ),
)
const Location = lazyLoad(
  () => import("./features/practiceProfile/OfficeInfo/components/Location"),
)
const LocationAddress = lazyLoad(() =>
  import("./features/practiceProfile/OfficeInfo/components/LocationAddress").then(
    (module) => ({ default: module.LocationAddress }),
  ),
)
const LocationContact = lazyLoad(() =>
  import("./features/practiceProfile/OfficeInfo/components/LocationContact").then(
    (module) => ({ default: module.LocationContact }),
  ),
)
const LocationDetails = lazyLoad(() =>
  import("./features/practiceProfile/OfficeInfo/components/LocationDetails").then(
    (module) => ({ default: module.LocationDetails }),
  ),
)
const AvailabilityWizard = lazyLoad(() =>
  import("./features/providerProfile/Availability/AvailabilityWizard").then(
    (module) => ({ default: module.AvailabilityWizard }),
  ),
)
const ClientFocusWizard = lazyLoad(() =>
  import("./features/providerProfile/ClientFocus/ClientFocusWizard").then(
    (module) => ({ default: module.ClientFocusWizard }),
  ),
)
const Disclosure = lazyLoad(
  () => import("./features/providerProfile/Disclosure/DisclosureV2"),
)
const Insurance = lazyLoad(() =>
  import("./features/providerProfile/Insurance/Insurance").then((module) => ({
    default: module.Insurance,
  })),
)

const ProviderLiabilityInsurance = lazyLoad(() =>
  import("./features/providerProfile/ProviderLiabilityInsurance/ProviderLiabilityInsurance").then(
    (module) => ({
      default: module.ProviderLiabilityInsurance,
    }),
  ),
)

const ProviderLocations = lazyLoad(() =>
  import("./features/providerProfile/ProviderLocations/ProviderLocations").then(
    (module) => ({
      default: module.ProviderLocations,
    }),
  ),
)

const ProviderServicesPage = lazyLoad(() =>
  import("./features/providerProfile/ProviderServices/ProviderServicesPage").then(
    (module) => ({ default: module.ProviderServicesPage }),
  ),
)

const PersonalInformationWizard = lazyLoad(() =>
  import("./features/providerProfile/PersonalInformation/PersonalInformationWizard").then(
    (module) => ({ default: module.PersonalInformationWizard }),
  ),
)
const QualificationsWizard = lazyLoad(() =>
  import("./features/providerProfile/Qualifications/QualificationsWizard").then(
    (module) => ({ default: module.QualificationsWizard }),
  ),
)
const ProviderProfilePage = lazyLoad(() =>
  import("./pages/ProviderProfile").then((module) => ({
    default: module.ProviderProfilePage,
  })),
)
const NoPracticeSelected = lazyLoad(() =>
  import("./pages/NoPracticeSelected").then((module) => ({
    default: module.NoPracticeSelected,
  })),
)
const NoProviderSelected = lazyLoad(() =>
  import("./pages/NoProviderSelected").then((module) => ({
    default: module.NoProviderSelected,
  })),
)
const ManagedPractices = lazyLoad(() =>
  import("./pages/ManagedPractices/ManagedPracticesPage").then((module) => ({
    default: module.ManagedPracticesPage,
  })),
)
const AffiliateRelationsActionsPageV2 = lazyLoad(() =>
  import("./pages/AffiliateRelationsActions/AffiliateRelationsActionsPageV2").then(
    (module) => ({ default: module.AffiliateRelationsActionsPageV2 }),
  ),
)
const PracticeRates = lazyLoad(() =>
  import("./pages/PracticeRates/PracticeRatesPage").then((module) => ({
    default: module.PracticeRates,
  })),
)
const PracticeInvitations = lazyLoad(() =>
  import("./pages/PracticeInvitations/PracticeInvitationsPage").then(
    (module) => ({ default: module.PracticeInvitations }),
  ),
)
const Taskboard = lazyLoad(() =>
  import("./pages/Taskboard/Taskboard").then((module) => ({
    default: module.Taskboard,
  })),
)
const Invitations = lazyLoad(() =>
  import("./pages/PracticeInvitations/Invitations").then((module) => ({
    default: module.Invitations,
  })),
)
const AcceptWrapper = lazyLoad(() =>
  import("./pages/PracticeInvitations/AcceptWrapper").then((module) => ({
    default: module.AcceptWrapper,
  })),
)
const GettingStarted = lazyLoad(
  () => import("./pages/GettingStarted/GettingStarted"),
)
const Activation = lazyLoad(() =>
  import("./pages/Activation").then((module) => ({
    default: module.Activation,
  })),
)
const AdvancedSearch = lazyLoad(() =>
  import("./pages/AdvancedSearch/AdvancedSearch").then((module) => ({
    default: module.AdvancedSearch,
  })),
)
const AdpSso = lazyLoad(() =>
  import("./pages/AdpSso").then((module) => ({
    default: module.AdpSso,
  })),
)
const Payments = lazyLoad(() =>
  import("./pages/Payments/Payments").then((module) => ({
    default: module.Payments,
  })),
)
const Referrals = lazyLoad(() =>
  import("./pages/Referrals/Referrals").then((module) => ({
    default: module.Referrals,
  })),
)
const ReferralList = lazyLoad(() =>
  import("./features/referrals/ReferralList").then((module) => ({
    default: module.ReferralList,
  })),
)
const ReferralInformation = lazyLoad(() =>
  import("./features/referrals/ReferralInformation").then((module) => ({
    default: module.ReferralInformation,
  })),
)
const PracticeIndividuals = lazyLoad(() =>
  import("./pages/PracticeIndividuals/PracticeIndividualsV2").then(
    (module) => ({
      default: module.PracticeIndividualsV2,
    }),
  ),
)
const ProviderSetup = lazyLoad(() =>
  import("./pages/ProviderSetup").then((module) => ({
    default: module.ProviderSetup,
  })),
)
const AdvancedSearchProvider = lazyLoad(
  () => import("./pages/AdvancedSearch/AdvancedSearchProvider"),
)
const SiteOutagePage = lazyLoad(() =>
  import("./pages/SiteOutagePage").then((module) => ({
    default: module.SiteOutagePage,
  })),
)
const OrphanedProviderPrompt = lazyLoad(() =>
  import("./pages/OrphanedProviderPrompt/OrphanedProviderPrompt").then(
    (module) => ({
      default: module.OrphanedProviderPrompt,
    }),
  ),
)
const OrphanedPracticeCreationFlowPlaceholder = lazyLoad(() =>
  import("./pages/OrphanedPracticeCreationFlowPlaceholder/OrphanedPracticeCreationFlowPlaceholder").then(
    (module) => ({
      default: module.OrphanedPracticeCreationFlowPlaceholder,
    }),
  ),
)
const OrphanedPracticeCreation = lazyLoad(() =>
  import("./pages/OrphanedPracticeCreation/OrphanedPracticeCreation").then(
    (module) => ({
      default: module.OrphanedPracticeCreation,
    }),
  ),
)
const CaseViewPage = lazyLoad(() =>
  import("./pages/CaseView/CaseViewPage").then((module) => ({
    default: module.CaseViewPage,
  })),
)

// TODO: remove this once the new dynamic table has been implemented somewhere else in the app and this test page is no longer needed
// const TestTable = lazyLoad(() =>
//   import("./features/shared/components/TableTestPage").then((module) => ({
//     default: module.TableTestPage,
//   })),
// )

/**
 * A custom hook that provides a navigation function with the ability to override the query string.
 *
 * @returns {Function} A function that allows navigation with an optional overridden query string.
 *
 * @example
 * const navigateWithQueryString = useNavigateWithQueryString();
 *
 * // Navigate with the current location's query string
 * navigateWithQueryString('/some-path');
 *
 * // Navigate with a custom query string
 * navigateWithQueryString('/some-path', '?param1=value1&param2=value2');
 */
export const useNavigateWithQueryString = () => {
  const navigate = useAppNavigate()

  /**
   * Navigates to the specified path with the option to override the query string.
   *
   * @param {string | Partial<Path>} path - The path to navigate to.
   * @param {string} [overrideSearchString] - The query string to override the current location's query string, must begin with '?'.
   * @param {NavigateOptions} [options] - Additional navigation options.
   *
   * @returns {void}
   */
  const navigateWithQueryString = (
    path: string | Partial<Path>,
    overrideSearchString?: string,
    options?: NavigateOptions,
  ) => {
    const currentSearch = window.location.search
    const searchString = overrideSearchString || currentSearch
    const routeWithParams = `${path}${searchString}`
    navigate(routeWithParams, options)
  }

  return navigateWithQueryString
}

export const buildNestedNode = (
  layoutElement: JSX.Element,
  children: { path: string; element: JSX.Element }[],
) => {
  return {
    element: layoutElement,
    children: children,
  }
}

export const buildNode = (path: string, element: JSX.Element) => {
  return {
    path: path,
    element: element,
  }
}

export const wrapWithHAUserAuthentication = (
  path: string,
  element: JSX.Element,
) => {
  return {
    path: path,
    element: <WithHaAdminAuthentication>{element}</WithHaAdminAuthentication>,
  }
}

// For main route, new variable should be made as needed for nested routes
export const HAUserAuthenticatedMainRoutes = [
  wrapWithHAUserAuthentication(appRoutes.USER_ROLES_ROUTE, <UserRoles />),
  // Support route now additionally guarded by HASupport wrapper
  wrapWithHAUserAuthentication(
    appRoutes.HA_SUPPORT_ROUTE,
    <WithHASupportAuthentication>
      <Support />
    </WithHASupportAuthentication>,
  ),
  // Add more routes here as needed
]

// Practice Info
const PracticeInfoElement = (
  <PracticeInformation>
    <Outlet />
  </PracticeInformation>
)

const PracticeInfoChildren = [
  {
    path: "address",
    element: <PracticeAddress />,
  },
  {
    path: "contact",
    element: <PracticeContact />,
  },
  {
    path: "details",
    element: <PracticeDetails />,
  },
]

const PracticeInfoNode = buildNestedNode(
  PracticeInfoElement,
  PracticeInfoChildren,
)

const LocationElement = (
  <Location>
    <Outlet />
  </Location>
)

const LocationChildren = [
  {
    path: "address",
    element: <LocationAddress />,
  },
  {
    path: "contact",
    element: <LocationContact />,
  },
  {
    path: "details",
    element: <LocationDetails />,
  },
]

const LocationNode = buildNestedNode(LocationElement, LocationChildren)
const ReferralElement = (
  <Referrals>
    <Outlet />
  </Referrals>
)
const ReferralChildren = [
  {
    path: "",
    element: <ReferralList />,
  },
  {
    path: "details",
    element: <ReferralInformation />,
  },
]
const ReferralNode = buildNestedNode(ReferralElement, ReferralChildren)

// Practice Profile Page
const PracticeProfileElement = (
  <PracticeProfilePage>
    <Outlet />
  </PracticeProfilePage>
)

const PracticeProfileChildren = [
  {
    path: "practiceInformation/*",
    element: <Outlet />,
    children: [PracticeInfoNode],
  },
  {
    path: "tax/*",
    element: <Outlet />,
    children: [
      {
        path: "",
        element: <TaxInformationBreadCrumbs />,
      },
      {
        path: "tin",
        element: (
          <TaxInformationBreadCrumbs>
            <TinVerification />
          </TaxInformationBreadCrumbs>
        ),
      },
      {
        path: "w9",
        element: (
          <TaxInformationBreadCrumbs>
            <W9Information />
          </TaxInformationBreadCrumbs>
        ),
      },
    ],
  },
  {
    path: "authandreleases",
    element: <Liability />,
  },
  {
    path: "liability",
    element: <Liability />,
  },
  {
    path: "offices",
    element: <Offices />,
  },
  {
    path: "location/*",
    element: <Outlet />,
    children: [LocationNode],
  },
  {
    path: "practiceAgreement",
    element: <PracticeAgreement />,
  },
]

const PracticeProfileNode = buildNestedNode(
  PracticeProfileElement,
  PracticeProfileChildren,
)

const PracticeInvitationsNode = buildNestedNode(
  <PracticeInvitations>
    <Outlet />
  </PracticeInvitations>,
  [
    {
      path: "",
      element: <Invitations />,
    },
    {
      path: "accept",
      element: <AcceptWrapper />,
    },
  ],
)

// Provider Profile Page
const ProviderProfileElement = (
  <ProviderProfilePage>
    <Outlet />
  </ProviderProfilePage>
)

const ProviderProfileChildren = [
  {
    path: "availability/newPatients",
    element: <AvailabilityWizard />,
  },
  {
    path: "availability/daysOfOperation",
    element: <AvailabilityWizard />,
  },
  {
    path: "availability/blockOutDates",
    element: <AvailabilityWizard />,
  },
  {
    path: "clientFocus/specialties",
    element: <ClientFocusWizard />,
  },
  {
    path: "clientFocus/demographics",
    element: <ClientFocusWizard />,
  },
  {
    path: "clientFocus/sessions",
    element: <ClientFocusWizard />,
  },
  {
    path: "disclosure",
    element: <Disclosure />,
  },
  {
    path: "insurance",
    element: <Insurance />,
  },
  {
    path: "locations",
    element: <ProviderLocations />,
  },
  {
    path: "services",
    element: <ProviderServicesPage />,
  },
  {
    path: "liabilityInsurance",
    element: <ProviderLiabilityInsurance />,
  },
  {
    path: "personalInformation/contact",
    element: <PersonalInformationWizard />,
  },
  {
    path: "personalInformation/bioAndPhoto",
    element: <PersonalInformationWizard />,
  },
  {
    path: "personalInformation/details",
    element: <PersonalInformationWizard />,
  },
  {
    path: "qualifications/licenses",
    element: <QualificationsWizard />,
  },
  {
    path: "qualifications/credentials",
    element: <QualificationsWizard />,
  },
  {
    path: "qualifications/education",
    element: <QualificationsWizard />,
  },
]

const ProviderProfileNode = buildNestedNode(
  ProviderProfileElement,
  ProviderProfileChildren,
)

//// Main App
export const MainContentLayoutElement = (
  <ErrorBoundary>
    <NavigationProvider>
      <ContentLayout>
        <WithAuthentication>
          <DataFetchingRouter>
            <NavigationListener>
              <AppScrollStrategyProvider>
                <Suspense fallback={<Spinner />}>
                  <Outlet />
                </Suspense>
              </AppScrollStrategyProvider>
            </NavigationListener>
          </DataFetchingRouter>
        </WithAuthentication>
      </ContentLayout>
    </NavigationProvider>
  </ErrorBoundary>
)

export const MainContentLayoutChildren = [
  {
    path: routes.BASE_ROUTE,
    element: <Loading />,
  },
  {
    path: "haLogin",
    element: <Landing />,
  },
  {
    path: "advancedSearch",
    element: (
      <AdvancedSearchProvider>
        <AdvancedSearch />
      </AdvancedSearchProvider>
    ),
  },
  {
    path: "dashboard",
    element: <Dashboard />,
  },
  {
    path: "termsOfUse",
    element: <TermsOfUse />,
  },
  {
    path: "contactUsPage",
    element: <ContactUs />,
  },
  {
    path: "legalAndPrivacyPage",
    element: <PrivacyPolicyPage />,
  },
  {
    path: "helpPage",
    element: <HelpPage />,
  },
  {
    path: "internalError",
    element: <InternalError />,
  },
  {
    path: "termsOfUsePage",
    element: <TermsOfUsePage />,
  },
  {
    path: "practice",
    element: <NoPracticeSelected />,
  },
  {
    path: "practiceInvitations/*",
    element: <Outlet />,
    children: [PracticeInvitationsNode],
  },
  {
    path: "affiliateRelationsActions",
    element: <AffiliateRelationsActionsPageV2 />,
  },
  {
    path: "practiceRates",
    element: <PracticeRates />,
  },
  {
    path: "practiceIndividuals",
    element: <PracticeIndividuals />,
  },
  {
    path: "taskboard",
    element: <Taskboard />,
  },
  {
    path: "taskboard/practice",
    element: <Taskboard />,
  },
  {
    path: "taskboard/provider",
    element: <Taskboard />,
  },
  {
    path: "referrals/*",
    element: <Outlet />,
    children: [ReferralNode],
  },
  {
    path: "provider",
    element: <NoProviderSelected />,
  },
  {
    path: "gettingStarted",
    element: <GettingStarted />,
  },
  {
    path: "activation",
    element: <Activation />,
  },
  {
    path: "payments",
    element: <Payments />,
  },
  {
    path: "adpSso",
    element: <AdpSso />,
  },
  {
    path: "providerSetup",
    element: <ProviderSetup />,
  },
  {
    path: "practice/*",
    element: <Outlet />,
    children: [PracticeProfileNode],
  },
  {
    path: "provider/*",
    element: <Outlet />,
    children: [ProviderProfileNode],
  },
  {
    path: "underConstruction",
    element: <UnderConstruction />,
  },
  {
    path: "managedPractices",
    element: <ManagedPractices />,
  },
  {
    path: "arSupport",
    element: <ARSupport />,
  },
  {
    path: "siteOutage",
    element: <SiteOutagePage />,
  },
  {
    path: "orphanedProviderPrompt",
    element: <OrphanedProviderPrompt />,
  },
  {
    path: "orphanedPracticeCreation",
    element: <OrphanedPracticeCreation />,
  },
  {
    path: "orphanedPracticeCreationFlowPlaceholder",
    element: <OrphanedPracticeCreationFlowPlaceholder />,
  },
  {
    path: "caseView",
    element: <CaseViewPage />,
  },
  ...HAUserAuthenticatedMainRoutes,
]

export const MainContentLayout = buildNestedNode(
  MainContentLayoutElement,
  MainContentLayoutChildren,
)

const QANode = buildNode(
  "QA",
  <Suspense fallback={<Spinner />}>
    <QA />
  </Suspense>,
)

const PageNotFound404 = buildNode(
  "*",
  <Suspense fallback={<Spinner />}>
    <ErrorMessage type="notFound" />
  </Suspense>,
)

const MainAppRoutes = [MainContentLayout, QANode, PageNotFound404]
const router = createBrowserRouter(MainAppRoutes)

const ESSENTIAL_COLLECTIONS: (keyof Content)[] = [
  "common",
  "navigation",
  "contentLayout",
  "withAuthentication",
  "publicProfileSlide",
  "systemMessages",
]

export function App() {
  const dispatch = useAppDispatch()

  const contentDataStatus = useSelector(selectContentDataStatus)
  const providerTableDataStatus = useSelector(selectProviderTableDataStatus)
  const practiceTableDataStatus = useSelector(selectPracticeTableDataStatus)
  const authenticationStatus = useSelector(selectAuthenticationStatus)

  const { isReady: cmsReady } = useCMSReady(ESSENTIAL_COLLECTIONS)

  // Memoize CMS content readiness check to prevent unnecessary recalculation
  const isCMSContentReady = useMemo(() => {
    return cmsReady
  }, [cmsReady])
  React.useEffect(() => {
    if (authenticationStatus === AuthStatus.Authenticated) {
      getProtectedConfig().then((response) => {
        // If we got a connection string, initialize AppInsights with it
        if (response?.app_insights_connection_string) {
          initializeAppInsights(response.app_insights_connection_string)
        }
      })
    }

    initializeCMSMode().then(() => {
      // Reinitialize file details with the correct CMS mode paths
      reinitializeFileDetails()

      appInsightsLogger.traceInfo(
        "🔄 Using CMS Indirectus with Lazy Loading (loadEssentialCollections)",
      )
      import("./domain/CMS/lazyCollectionLoader").then(
        ({ loadEssentialCollections }) => {
          dispatch(loadEssentialCollections())
        },
      )
    })
    if (
      providerTableDataStatus === DataStatus.LOADING &&
      authenticationStatus === AuthStatus.Authenticated
    ) {
      dispatch(fetchProviderTableData())
    }
    if (
      practiceTableDataStatus === DataStatus.LOADING &&
      authenticationStatus === AuthStatus.Authenticated
    ) {
      dispatch(fetchPracticeTableData())
    }
  }, [
    authenticationStatus,
    contentDataStatus,
    dispatch,
    practiceTableDataStatus,
    providerTableDataStatus,
  ])

  if (contentDataStatus === DataStatus.ERROR)
    return (
      <ErrorMessage
        sessionIdMessage={`SESSION ID: ${appInsights.context.sessionManager.automaticSession.id}`}
      />
    )

  if (!isCMSContentReady) return <Loading />

  return <RouterProvider router={router} />
}

//// Utility
interface Node {
  path?: string
  children?: Node[]
}
export const extractPaths = (node: Node): string[] => {
  let paths: string[] = []
  if (node.path) {
    paths.push(node.path)
  }
  if (node.children) {
    node.children.forEach((child) => {
      paths = paths.concat(extractPaths(child))
    })
  }
  return paths
}
